import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ModalDialogComponent } from '@vg-constellation/angular-16/modal-dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject, auditTime, distinctUntilChanged, fromEvent, map, takeUntil } from 'rxjs';
import { ButtonIconPosition } from '@vg-constellation/angular-16/button';

@Component({
  selector: 'app-product-intro-banner',
  templateUrl: './product-intro-banner.component.html',
  styleUrls: ['./product-intro-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductIntroBannerComponent implements OnInit, OnDestroy {
  private readonly STACKED_BUTTON_BREAKPOINT = 767;
  private readonly DEFAULT_HEADING_ID = 'product-intro-banner-heading';
  private isStackedButtonLayoutState = false;
  private isVideoPlayerVisibleState = false;
  private isTranscriptExpandedState = false;
  private readonly destroy$ = new Subject<void>();

  @Input() eyebrow = '';
  @Input() headline = '';
  @Input() description = '';
  @Input() descriptionSecondParagraph = '';
  @Input() primaryCtaLabel = '';
  @Input() primaryCtaVariant: 'primary' | 'secondary' = 'primary';
  @Input() primaryCtaClass = '';
  @Input() primaryCtaIconName = '';
  @Input() primaryCtaIconPosition: ButtonIconPosition = 'trailing';
  @Input() secondaryCtaLabel = '';
  @Input() mediaImageSrc = '';
  @Input() mediaImageAlt = '';
  @Input() videoSrc = '';
  @Input() videoTitle = '';
  @Input() watchDemoAriaLabel = '';
  @Input() headingId = '';

  /**
   * Absolute `https://` URL to open in a new tab. Non-https or relative URLs
   * are rejected and `primaryCtaClick` is emitted instead.
   */
  @Input() primaryCtaUrl = '';

  @Output() primaryCtaClick = new EventEmitter<void>();

  @ViewChild('productIntroVideoModalDialog', { static: false })
  productIntroVideoModalDialog?: ModalDialogComponent;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly cdr: ChangeDetectorRef,
    private readonly translateService: TranslateService,
  ) { }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.setViewportWidth();

    fromEvent(window, 'resize')
      .pipe(
        auditTime(100),
        map(() => window.innerWidth <= this.STACKED_BUTTON_BREAKPOINT),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((isStacked) => {
        this.isStackedButtonLayoutState = isStacked;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isStackedButtonLayout(): boolean {
    return this.isStackedButtonLayoutState;
  }

  get resolvedVideoTitle(): string {
    return this.videoTitle || this.translateService.instant('shared.productIntroBanner.videoTitle');
  }

  get resolvedHeadingId(): string {
    const id = this.headingId.trim();
    return id || this.DEFAULT_HEADING_ID;
  }

  get resolvedWatchDemoAriaLabel(): string {
    const explicitAriaLabel = this.watchDemoAriaLabel.trim();
    if (explicitAriaLabel) {
      return explicitAriaLabel;
    }

    const secondaryLabel = this.secondaryCtaLabel.trim();
    if (secondaryLabel) {
      return secondaryLabel;
    }

    return this.translateService.instant('shared.productIntroBanner.watchDemo');
  }

  get hasVideoSource(): boolean {
    return this.videoSrc.trim().length > 0;
  }

  get isVideoPlayerVisible(): boolean {
    return this.isVideoPlayerVisibleState;
  }

  get isTranscriptExpanded(): boolean {
    return this.isTranscriptExpandedState;
  }

  onPrimaryCtaClick(): void {
    const url = this.primaryCtaUrl.trim();
    if (url && isPlatformBrowser(this.platformId) && this.isSafeUrl(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    this.primaryCtaClick.emit();
  }

  onWatchDemoClick(): void {
    if (!this.hasVideoSource || !this.productIntroVideoModalDialog) {
      return;
    }

    this.isVideoPlayerVisibleState = true;

    try {
      this.productIntroVideoModalDialog.openModalDialog();
      // Skip time(0) — position is already reset by stopWistiaPlayer() on close; seeking here triggers Wistia's buffering spinner.
    } catch {
      // openModalDialog() threw synchronously; keep video player hidden so the UI stays consistent
      this.isVideoPlayerVisibleState = false;
    }

    this.cdr.markForCheck();
  }

  onVideoModalClosing(): void {
    // Fires before closeEvent (dialog still visible) so player API calls are live; stops Wistia before close animation.
    this.stopWistiaPlayer();
  }

  onTranscriptToggle(): void {
    this.isTranscriptExpandedState = !this.isTranscriptExpandedState;
    this.cdr.markForCheck();
  }

  onVideoModalClose(): void {
    // Fires after the dialog has fully closed — only update Angular UI state.
    this.isVideoPlayerVisibleState = false;
    this.isTranscriptExpandedState = false;
    this.cdr.markForCheck();
  }

  private stopWistiaPlayer(): void {
    try {
      // Try the standard Wistia JS API first.
      const player = (window as any).Wistia?.api(this.videoSrc);
      if (player) {
        player.pause();
        player.time(0);
        return;
      }
      // Fallback: find the <video> in the web component's shadow DOM (Wistia renders HTML5 <video> for inline embeds).
      const webComponent = document.querySelector('wistia-player');
      const videoEl = (webComponent?.shadowRoot?.querySelector('video')
        ?? webComponent?.querySelector('video')) as HTMLVideoElement | null;
      if (videoEl) {
        videoEl.pause();
        videoEl.currentTime = 0;
        return;
      }
      // Last-resort fallback: pause any playing <video> scoped to c11n-modal-dialog to avoid affecting unrelated page videos.
      document.querySelectorAll<HTMLVideoElement>('c11n-modal-dialog video').forEach((v) => {
        if (!v.paused) {
          v.pause();
        }
      });
    } catch (error) {
      console.warn('[ProductIntroBanner] Failed to stop video player during modal close:', error);
    }
  }

  private setViewportWidth(): void {
    this.isStackedButtonLayoutState = window.innerWidth <= this.STACKED_BUTTON_BREAKPOINT;
  }

  private isSafeUrl(url: string): boolean {
    try {
      return new URL(url).protocol === 'https:';
    } catch {
      return false;
    }
  }
}
